import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { 
  Ticket, 
  Users, 
  Calendar, 
  DollarSign, 
  Globe, 
  MapPin, 
  Camera,
  FileText,
  Shield,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Upload,
  Star,
  TrendingUp,
  Clock,
  ChevronDown,
  Check,
  Utensils,
  Palette,
  Building,
  Heart,
  Wallet,
  BadgeCheck,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// Hero background image - professional tour guide
const HERO_BG = "https://images.unsplash.com/photo-1652572242726-521063ab7179?auto=format&fit=crop&w=1920&q=80";

const BENEFITS = [
  {
    icon: Users,
    title: "Strong Network",
    description: "Connect with tour operators who need your expertise, even during the low season."
  },
  {
    icon: Wallet,
    title: "Secure Payments",
    description: "Get paid securely and quickly through our integrated Stripe payment system."
  },
  {
    icon: Calendar,
    title: "Year-Round Bookings",
    description: "Receive a steady stream of reservations throughout the entire year."
  },
  {
    icon: Clock,
    title: "Easy Management",
    description: "Manage your availability and accept requests with a simple dashboard."
  },
  {
    icon: TrendingUp,
    title: "Grow Your Business",
    description: "Expand your client base and increase your income potential."
  },
  {
    icon: BadgeCheck,
    title: "Build Reputation",
    description: "Receive reviews and build trust with tour operators worldwide."
  }
];

const LANGUAGES = [
  "English", "Italian", "Spanish", "French", "German", "Portuguese",
  "Chinese", "Japanese", "Korean", "Russian", "Arabic", "Dutch",
  "Greek", "Turkish", "Polish", "Swedish", "Norwegian", "Danish",
  "Finnish", "Czech", "Hungarian", "Romanian", "Bulgarian", "Croatian"
];

const SERVICE_TYPES = [
  { id: "monument_tour", label: "Monument Tour", icon: Building },
  { id: "food_tour", label: "Food & Wine", icon: Utensils },
  { id: "cooking_class", label: "Cooking Class", icon: Utensils },
  { id: "art_tour", label: "Art & Culture", icon: Palette },
  { id: "walking_tour", label: "Walking Tour", icon: MapPin },
  { id: "private_guide", label: "Private Guide", icon: Users },
  { id: "photography_tour", label: "Photography", icon: Camera },
  { id: "local_experience", label: "Local Experience", icon: Heart },
];

// Country list with flags and phone codes
const COUNTRIES = [
  { code: "IT", name: "Italy", phone: "+39", flag: "🇮🇹" },
  { code: "US", name: "United States", phone: "+1", flag: "🇺🇸" },
  { code: "GB", name: "United Kingdom", phone: "+44", flag: "🇬🇧" },
  { code: "FR", name: "France", phone: "+33", flag: "🇫🇷" },
  { code: "DE", name: "Germany", phone: "+49", flag: "🇩🇪" },
  { code: "ES", name: "Spain", phone: "+34", flag: "🇪🇸" },
  { code: "PT", name: "Portugal", phone: "+351", flag: "🇵🇹" },
  { code: "NL", name: "Netherlands", phone: "+31", flag: "🇳🇱" },
  { code: "BE", name: "Belgium", phone: "+32", flag: "🇧🇪" },
  { code: "CH", name: "Switzerland", phone: "+41", flag: "🇨🇭" },
  { code: "AT", name: "Austria", phone: "+43", flag: "🇦🇹" },
  { code: "GR", name: "Greece", phone: "+30", flag: "🇬🇷" },
  { code: "TR", name: "Turkey", phone: "+90", flag: "🇹🇷" },
  { code: "PL", name: "Poland", phone: "+48", flag: "🇵🇱" },
  { code: "CZ", name: "Czech Republic", phone: "+420", flag: "🇨🇿" },
  { code: "HU", name: "Hungary", phone: "+36", flag: "🇭🇺" },
  { code: "HR", name: "Croatia", phone: "+385", flag: "🇭🇷" },
  { code: "RO", name: "Romania", phone: "+40", flag: "🇷🇴" },
  { code: "BG", name: "Bulgaria", phone: "+359", flag: "🇧🇬" },
  { code: "SE", name: "Sweden", phone: "+46", flag: "🇸🇪" },
  { code: "NO", name: "Norway", phone: "+47", flag: "🇳🇴" },
  { code: "DK", name: "Denmark", phone: "+45", flag: "🇩🇰" },
  { code: "FI", name: "Finland", phone: "+358", flag: "🇫🇮" },
  { code: "IE", name: "Ireland", phone: "+353", flag: "🇮🇪" },
  { code: "RU", name: "Russia", phone: "+7", flag: "🇷🇺" },
  { code: "UA", name: "Ukraine", phone: "+380", flag: "🇺🇦" },
  { code: "JP", name: "Japan", phone: "+81", flag: "🇯🇵" },
  { code: "CN", name: "China", phone: "+86", flag: "🇨🇳" },
  { code: "KR", name: "South Korea", phone: "+82", flag: "🇰🇷" },
  { code: "AU", name: "Australia", phone: "+61", flag: "🇦🇺" },
  { code: "NZ", name: "New Zealand", phone: "+64", flag: "🇳🇿" },
  { code: "CA", name: "Canada", phone: "+1", flag: "🇨🇦" },
  { code: "MX", name: "Mexico", phone: "+52", flag: "🇲🇽" },
  { code: "BR", name: "Brazil", phone: "+55", flag: "🇧🇷" },
  { code: "AR", name: "Argentina", phone: "+54", flag: "🇦🇷" },
  { code: "CL", name: "Chile", phone: "+56", flag: "🇨🇱" },
  { code: "CO", name: "Colombia", phone: "+57", flag: "🇨🇴" },
  { code: "PE", name: "Peru", phone: "+51", flag: "🇵🇪" },
  { code: "EG", name: "Egypt", phone: "+20", flag: "🇪🇬" },
  { code: "ZA", name: "South Africa", phone: "+27", flag: "🇿🇦" },
  { code: "MA", name: "Morocco", phone: "+212", flag: "🇲🇦" },
  { code: "TN", name: "Tunisia", phone: "+216", flag: "🇹🇳" },
  { code: "AE", name: "United Arab Emirates", phone: "+971", flag: "🇦🇪" },
  { code: "SA", name: "Saudi Arabia", phone: "+966", flag: "🇸🇦" },
  { code: "IL", name: "Israel", phone: "+972", flag: "🇮🇱" },
  { code: "IN", name: "India", phone: "+91", flag: "🇮🇳" },
  { code: "TH", name: "Thailand", phone: "+66", flag: "🇹🇭" },
  { code: "VN", name: "Vietnam", phone: "+84", flag: "🇻🇳" },
  { code: "ID", name: "Indonesia", phone: "+62", flag: "🇮🇩" },
  { code: "MY", name: "Malaysia", phone: "+60", flag: "🇲🇾" },
  { code: "SG", name: "Singapore", phone: "+65", flag: "🇸🇬" },
  { code: "PH", name: "Philippines", phone: "+63", flag: "🇵🇭" },
].sort((a, b) => a.name.localeCompare(b.name));

const GuideJoinPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [monuments, setMonuments] = useState([]);
  
  // Dropdown open states
  const [countryOpen, setCountryOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    country_code: '',
    city_id: '',
    city_name: '',
    monument_ids: [],
    languages: [],
    service_types: [],
    hourly_rate_eur: '',
    license_number: '',
    about_me: '',
  });
  
  const [files, setFiles] = useState({
    profile_photo: null,
    license_document: null,
    badge_document: null,
  });
  
  const [previews, setPreviews] = useState({
    profile_photo: null,
  });
  
  // Load cities when country changes
  useEffect(() => {
    const loadCities = async () => {
      if (form.country_code) {
        try {
          const country = COUNTRIES.find(c => c.code === form.country_code);
          const countryName = country?.name || '';
          const response = await api.get(`/public/cities?country=${encodeURIComponent(countryName)}`);
          setCities(response.data);
        } catch (error) {
          console.error('Failed to load cities:', error);
          setCities([]);
        }
      } else {
        setCities([]);
      }
    };
    loadCities();
  }, [form.country_code]);
  
  // Load monuments when city changes
  useEffect(() => {
    const loadMonuments = async () => {
      if (form.city_id) {
        try {
          const response = await api.get(`/public/monuments?city_id=${form.city_id}`);
          setMonuments(response.data);
        } catch (error) {
          console.error('Failed to load monuments:', error);
        }
      } else {
        setMonuments([]);
      }
    };
    loadMonuments();
  }, [form.city_id]);
  
  // Get selected country
  const selectedCountry = useMemo(() => {
    return COUNTRIES.find(c => c.code === form.country_code);
  }, [form.country_code]);
  
  // Get selected city
  const selectedCity = useMemo(() => {
    return cities.find(c => c.id === form.city_id);
  }, [form.city_id, cities]);
  
  // Service types that require monument selection
  const MONUMENT_RELATED_SERVICES = ['monument_tour', 'art_tour', 'private_guide'];
  
  // Check if any monument-related service is selected
  const showMonumentSelection = useMemo(() => {
    return form.service_types.some(s => MONUMENT_RELATED_SERVICES.includes(s));
  }, [form.service_types]);
  
  const handleFileChange = (field, file) => {
    setFiles(prev => ({ ...prev, [field]: file }));
    
    if (field === 'profile_photo' && file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => ({ ...prev, profile_photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleLanguageToggle = (lang) => {
    setForm(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };
  
  const handleServiceToggle = (serviceId) => {
    setForm(prev => ({
      ...prev,
      service_types: prev.service_types.includes(serviceId)
        ? prev.service_types.filter(s => s !== serviceId)
        : [...prev.service_types, serviceId]
    }));
  };
  
  const handleMonumentToggle = (monumentId) => {
    setForm(prev => ({
      ...prev,
      monument_ids: prev.monument_ids.includes(monumentId)
        ? prev.monument_ids.filter(m => m !== monumentId)
        : [...prev.monument_ids, monumentId]
    }));
  };
  
  const handleCountrySelect = (countryCode) => {
    setForm(prev => ({
      ...prev,
      country_code: countryCode,
      city_id: '',
      city_name: '',
      monument_ids: [],
    }));
    setCities([]);
    setMonuments([]);
    setCountryOpen(false);
  };
  
  const handleCitySelect = (cityId) => {
    const city = cities.find(c => c.id === cityId);
    setForm(prev => ({
      ...prev,
      city_id: cityId,
      city_name: city?.name || '',
      monument_ids: [],
    }));
    setCityOpen(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!files.profile_photo) {
      toast.error('Profile photo is required');
      return;
    }
    
    if (!form.country_code) {
      toast.error('Please select your country');
      return;
    }
    
    if (form.languages.length === 0) {
      toast.error('Please select at least one language');
      return;
    }
    
    if (form.service_types.length === 0) {
      toast.error('Please select at least one service type');
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('full_name', form.full_name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('city', form.city_name || selectedCity?.name || '');
      formData.append('country', selectedCountry?.name || '');
      formData.append('monument_ids', form.monument_ids.join(','));
      formData.append('languages', form.languages.join(','));
      formData.append('service_types', form.service_types.join(','));
      formData.append('hourly_rate_eur', form.hourly_rate_eur);
      formData.append('license_number', form.license_number || '');
      formData.append('about_me', form.about_me);
      formData.append('profile_photo', files.profile_photo);
      
      if (files.license_document) {
        formData.append('license_document', files.license_document);
      }
      if (files.badge_document) {
        formData.append('badge_document', files.badge_document);
      }
      
      const response = await api.post('/guide-apply', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Application submitted! Check your email to verify your address.');
      navigate(`/guide/application-status/${response.data.application_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== STEP 1: HERO & BENEFITS ====================
  if (step === 1) {
    return (
      <div className="min-h-screen bg-stone-50">
        {/* Hero Section */}
        <div className="relative min-h-[90vh] overflow-hidden">
          {/* Background Image with Overlay */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_BG})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900/90 via-teal-800/80 to-stone-900/70" />
          
          {/* Decorative Elements */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 h-full flex flex-col">
            {/* Navigation */}
            <nav className="flex items-center justify-between p-6 lg:px-12">
              <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-all">
                  <Ticket className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-white tracking-tight">Muoov</span>
              </Link>
              <Link to="/">
                <Button 
                  variant="outline" 
                  className="bg-white/5 backdrop-blur-md border-white/20 text-white hover:bg-white/10 rounded-full px-6"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </nav>
            
            {/* Hero Content */}
            <div className="flex-1 flex items-center justify-center px-4 py-12">
              <div className="max-w-4xl text-center">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-orange-500/20 backdrop-blur-sm border border-orange-400/30 rounded-full px-5 py-2 mb-8">
                  <Sparkles className="w-4 h-4 text-orange-300" />
                  <span className="text-orange-200 font-medium text-sm">Join 500+ Professional Guides</span>
                </div>
                
                {/* Headline */}
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                  Share Your World.
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-amber-300 to-yellow-300">
                    Inspire Travelers.
                  </span>
                </h1>
                
                {/* Subheadline */}
                <p className="text-lg md:text-xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
                  Join the premier network of professional tour guides. Connect with operators, 
                  manage bookings effortlessly, and grow your guiding business.
                </p>
                
                {/* CTA Button */}
                <Button 
                  size="lg"
                  onClick={() => setStep(2)}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-xl shadow-orange-500/25 h-14 px-10 text-lg rounded-full font-semibold transition-all hover:scale-105"
                  data-testid="start-application-btn"
                >
                  Start Your Application
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                {/* Trust Indicators */}
                <div className="mt-12 flex items-center justify-center gap-8 text-white/50 text-sm">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>Verified Platform</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    <span>30+ Countries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    <span>4.9/5 Rating</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Benefits Section */}
        <div className="py-24 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <Badge className="bg-teal-100 text-teal-700 border-teal-200 mb-4">
                Why Choose Muoov
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4 tracking-tight">
                Everything You Need to Succeed
              </h2>
              <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
                Our platform empowers professional guides with the tools and connections 
                they need to thrive in the tourism industry.
              </p>
            </div>
            
            {/* Benefits Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {BENEFITS.map((benefit, idx) => (
                <Card 
                  key={idx} 
                  className="group border border-stone-100 bg-white hover:border-teal-200 hover:shadow-lg hover:shadow-teal-500/5 transition-all duration-300 rounded-2xl overflow-hidden"
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <benefit.icon className="w-7 h-7 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-stone-900 mb-3">{benefit.title}</h3>
                    <p className="text-stone-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Bottom CTA */}
            <div className="text-center mt-16">
              <Button 
                size="lg"
                onClick={() => setStep(2)}
                className="bg-teal-700 hover:bg-teal-800 text-white shadow-lg shadow-teal-700/20 h-14 px-10 text-lg rounded-full font-semibold"
                data-testid="apply-now-btn"
              >
                Apply Now - It's Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="mt-4 text-stone-500 text-sm">No fees to join. Start earning today.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // ==================== STEP 2: APPLICATION FORM ====================
  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => setStep(1)}
            className="text-stone-600 hover:text-stone-900 rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-teal-700 rounded-xl flex items-center justify-center">
              <Ticket className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-stone-900">Muoov</span>
          </Link>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </header>
      
      {/* Form Container */}
      <div className="max-w-3xl mx-auto px-4 py-8 pb-24">
        {/* Form Header */}
        <div className="text-center mb-10">
          <Badge className="bg-teal-100 text-teal-700 border-teal-200 mb-4">
            Step 2 of 2
          </Badge>
          <h1 className="text-3xl font-bold text-stone-900 mb-2 tracking-tight">Complete Your Profile</h1>
          <p className="text-stone-600">Fill in your details to join our network of professional guides</p>
        </div>
        
        {/* Main Form Card */}
        <Card className="shadow-xl border-0 rounded-3xl overflow-hidden">
          <CardContent className="p-0">
            <form onSubmit={handleSubmit}>
              {/* Profile Photo Section */}
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 p-8 text-center">
                <div className="relative inline-block">
                  <div className="w-32 h-32 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white/30 overflow-hidden shadow-2xl">
                    {previews.profile_photo ? (
                      <img src={previews.profile_photo} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-white/10">
                        <Camera className="w-12 h-12 text-white/60" />
                      </div>
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 w-10 h-10 bg-orange-500 hover:bg-orange-600 rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-colors">
                    <Upload className="w-5 h-5 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileChange('profile_photo', e.target.files[0])}
                      data-testid="profile-photo-input"
                    />
                  </label>
                </div>
                <p className="text-white/80 text-sm mt-4">Upload your professional photo</p>
              </div>
              
              <div className="p-8 space-y-10">
                {/* Personal Information */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                      <Users className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">Personal Information</h2>
                      <p className="text-sm text-stone-500">Basic details about you</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-stone-700 font-medium">Full Name *</Label>
                      <Input
                        required
                        value={form.full_name}
                        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                        placeholder="John Smith"
                        className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                        data-testid="full-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-stone-700 font-medium">Email Address *</Label>
                      <Input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="john@example.com"
                        className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-stone-700 font-medium">Phone Number *</Label>
                      <PhoneInput
                        international
                        defaultCountry="IT"
                        value={form.phone}
                        onChange={(value) => setForm({ ...form, phone: value || '' })}
                        className="flex h-12 w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-500"
                        data-testid="phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-stone-700 font-medium">Hourly Rate (€) *</Label>
                      <Input
                        type="number"
                        min="1"
                        step="0.01"
                        required
                        value={form.hourly_rate_eur}
                        onChange={(e) => setForm({ ...form, hourly_rate_eur: e.target.value })}
                        placeholder="50"
                        className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                        data-testid="hourly-rate-input"
                      />
                    </div>
                  </div>
                </section>
                
                {/* Location Section */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">Your Location</h2>
                      <p className="text-sm text-stone-500">Where do you operate?</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-5">
                    {/* Country Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-stone-700 font-medium">Country *</Label>
                      <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={countryOpen}
                            className="w-full h-12 justify-between font-normal rounded-xl border-stone-200 hover:bg-stone-50"
                            data-testid="country-select"
                          >
                            {selectedCountry ? (
                              <span className="flex items-center gap-2">
                                <span className="text-xl">{selectedCountry.flag}</span>
                                {selectedCountry.name}
                              </span>
                            ) : (
                              <span className="text-stone-400">Select your country...</span>
                            )}
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0 rounded-xl" align="start">
                          <Command>
                            <CommandInput placeholder="Search country..." className="h-11" />
                            <CommandList>
                              <CommandEmpty>No country found.</CommandEmpty>
                              <CommandGroup className="max-h-64 overflow-auto">
                                {COUNTRIES.map((country) => (
                                  <CommandItem
                                    key={country.code}
                                    value={country.name}
                                    onSelect={() => handleCountrySelect(country.code)}
                                    className="flex items-center gap-3 py-3"
                                  >
                                    <span className="text-xl">{country.flag}</span>
                                    <span className="flex-1">{country.name}</span>
                                    <span className="text-stone-400 text-sm">{country.phone}</span>
                                    {form.country_code === country.code && (
                                      <Check className="h-4 w-4 text-teal-600" />
                                    )}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    {/* City Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-stone-700 font-medium">City *</Label>
                      <Popover open={cityOpen} onOpenChange={setCityOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={cityOpen}
                            className="w-full h-12 justify-between font-normal rounded-xl border-stone-200 hover:bg-stone-50"
                            disabled={!form.country_code}
                            data-testid="city-select"
                          >
                            {selectedCity ? (
                              <span className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-teal-600" />
                                {selectedCity.name}
                              </span>
                            ) : (
                              <span className="text-stone-400">
                                {!form.country_code 
                                  ? 'Select country first...' 
                                  : cities.length === 0 
                                    ? 'No cities available' 
                                    : 'Select your city...'}
                              </span>
                            )}
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0 rounded-xl" align="start">
                          <Command>
                            <CommandInput placeholder="Search city..." className="h-11" />
                            <CommandList>
                              <CommandEmpty>No cities found. Contact support to add your city.</CommandEmpty>
                              <CommandGroup className="max-h-64 overflow-auto">
                                {cities.map((city) => (
                                  <CommandItem
                                    key={city.id}
                                    value={city.name}
                                    onSelect={() => handleCitySelect(city.id)}
                                    className="flex items-center gap-3 py-3"
                                  >
                                    <MapPin className="w-4 h-4 text-stone-400" />
                                    <span className="flex-1">{city.name}</span>
                                    {city.country && (
                                      <span className="text-stone-400 text-sm">{city.country}</span>
                                    )}
                                    {form.city_id === city.id && (
                                      <Check className="h-4 w-4 text-teal-600" />
                                    )}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </section>
                
                {/* Services Section */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Star className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">Services You Offer *</h2>
                      <p className="text-sm text-stone-500">Select all that apply</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {SERVICE_TYPES.map(service => (
                      <button
                        type="button"
                        key={service.id}
                        onClick={() => handleServiceToggle(service.id)}
                        className={`p-4 rounded-2xl border-2 cursor-pointer transition-all text-center ${
                          form.service_types.includes(service.id)
                            ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600'
                            : 'border-stone-100 bg-white hover:border-stone-200 hover:bg-stone-50'
                        }`}
                        data-testid={`service-${service.id}`}
                      >
                        <div className="flex flex-col items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                            form.service_types.includes(service.id)
                              ? 'bg-teal-600 text-white'
                              : 'bg-stone-100 text-stone-500'
                          }`}>
                            <service.icon className="w-6 h-6" />
                          </div>
                          <span className={`text-sm font-medium ${
                            form.service_types.includes(service.id)
                              ? 'text-teal-700'
                              : 'text-stone-700'
                          }`}>
                            {service.label}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
                
                {/* Monument Selection - Conditional */}
                {showMonumentSelection && form.city_id && monuments.length > 0 && (
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                        <Building className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-stone-900">Monuments You Guide At</h2>
                        <p className="text-sm text-stone-500">Select where you provide services</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 p-4 bg-stone-50 rounded-2xl">
                      {monuments.map(monument => (
                        <Badge
                          key={monument.id}
                          variant={form.monument_ids.includes(monument.id) ? "default" : "outline"}
                          className={`cursor-pointer transition-all py-2 px-4 text-sm ${
                            form.monument_ids.includes(monument.id) 
                              ? 'bg-teal-600 hover:bg-teal-700 text-white border-teal-600' 
                              : 'bg-white hover:bg-stone-100 text-stone-700 border-stone-200'
                          }`}
                          onClick={() => handleMonumentToggle(monument.id)}
                          data-testid={`monument-${monument.id}`}
                        >
                          <Building className="w-3 h-3 mr-2" />
                          {monument.name}
                          {form.monument_ids.includes(monument.id) && (
                            <CheckCircle className="w-3 h-3 ml-2" />
                          )}
                        </Badge>
                      ))}
                    </div>
                  </section>
                )}
                
                {/* Languages Section */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                      <Globe className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">Languages Spoken *</h2>
                      <p className="text-sm text-stone-500">Select all languages you can guide in</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES.map(lang => (
                      <Badge
                        key={lang}
                        variant={form.languages.includes(lang) ? "default" : "outline"}
                        className={`cursor-pointer transition-all py-2 px-4 ${
                          form.languages.includes(lang) 
                            ? 'bg-teal-600 hover:bg-teal-700 text-white border-teal-600' 
                            : 'bg-white hover:bg-stone-50 text-stone-600 border-stone-200'
                        }`}
                        onClick={() => handleLanguageToggle(lang)}
                        data-testid={`lang-${lang}`}
                      >
                        {lang}
                        {form.languages.includes(lang) && (
                          <CheckCircle className="w-3 h-3 ml-1.5" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </section>
                
                {/* About Section */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                      <FileText className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">About You *</h2>
                      <p className="text-sm text-stone-500">Tell us about your experience</p>
                    </div>
                  </div>
                  
                  <Textarea
                    required
                    rows={5}
                    value={form.about_me}
                    onChange={(e) => setForm({ ...form, about_me: e.target.value })}
                    placeholder="Share your background, qualifications, certifications, and what makes you a great guide..."
                    className="resize-none rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                    data-testid="about-me-input"
                  />
                </section>
                
                {/* Documents Section */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                      <Shield className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">Documents & Credentials</h2>
                      <p className="text-sm text-stone-500">Optional but recommended</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-stone-700 font-medium">License Number</Label>
                      <Input
                        value={form.license_number}
                        onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                        placeholder="Your guide license number"
                        className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                        data-testid="license-number-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-stone-700 font-medium">License Document</Label>
                      <label
                        className={`flex items-center gap-3 px-4 h-12 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                          files.license_document 
                            ? 'border-teal-300 bg-teal-50' 
                            : 'border-stone-200 hover:border-teal-300 hover:bg-stone-50'
                        }`}
                      >
                        <Upload className={`w-5 h-5 ${files.license_document ? 'text-teal-600' : 'text-stone-400'}`} />
                        <span className={`text-sm truncate ${files.license_document ? 'text-teal-700' : 'text-stone-500'}`}>
                          {files.license_document ? files.license_document.name : 'Upload license document'}
                        </span>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange('license_document', e.target.files[0])}
                          className="hidden"
                          data-testid="license-doc-input"
                        />
                      </label>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-stone-700 font-medium">Monument Badge Document</Label>
                    <label
                      className={`flex items-center gap-3 px-4 h-12 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                        files.badge_document 
                          ? 'border-teal-300 bg-teal-50' 
                          : 'border-stone-200 hover:border-teal-300 hover:bg-stone-50'
                      }`}
                    >
                      <Upload className={`w-5 h-5 ${files.badge_document ? 'text-teal-600' : 'text-stone-400'}`} />
                      <span className={`text-sm truncate ${files.badge_document ? 'text-teal-700' : 'text-stone-500'}`}>
                        {files.badge_document ? files.badge_document.name : 'Upload badge document (if applicable)'}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange('badge_document', e.target.files[0])}
                        className="hidden"
                        data-testid="badge-doc-input"
                      />
                    </label>
                  </div>
                </section>
                
                {/* Submit Section */}
                <section className="pt-6">
                  <Alert className="bg-teal-50 border-teal-200 rounded-xl mb-6">
                    <Shield className="h-4 w-4 text-teal-600" />
                    <AlertDescription className="text-teal-700">
                      After submitting, you'll receive an email to verify your address. 
                      Our team will review your application within 24-48 hours.
                    </AlertDescription>
                  </Alert>
                  
                  <Button
                    type="submit"
                    className="w-full h-14 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white shadow-lg shadow-teal-600/25 rounded-xl text-lg font-semibold"
                    disabled={loading}
                    data-testid="submit-application-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting Application...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </section>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GuideJoinPage;
